import * as FluxUtils from 'flux/utils';

import { AppDispatcher, DispatcherBaseAction } from '../dispatcher/AppDispatcher';
import { PCON_LOGIN_CLIENT_ID } from '../constants/Api';
import { PconLoginActions } from '../actions/PconLoginActions';
import { ActionConstants } from '../constants/ActionConstants';
// import { AppConfigurationStore } from './AppConfigurationStore';
import { i18n as i18nextInstance } from '..';

// Submodules
import { Dict } from '../submodules/utils/Types';
import { SettingProvider } from '../submodules/pcon-login-api/SettingsProvider';

import utils = egr.wcf.utils;
import { CustomLoginStore } from './CustomLoginStore';

// Minimum information required to identify the pCon.login user.
// (Strictly speaking, the loginId would suffice, but we need the loginName
// for the error/information texts.)
export interface PconLoginIdentifier {
    loginId: string;
    loginName: string | null;
    loginEmail?: string | null;
}

export interface PconLoginUser {
    uuid: string;
    title: string;
    login: string;
    email: string;
    organization: string;
    phone: string;
    first_name: string;
    last_name: string;
    language_code: string;
}

export interface PconLoginOrganization {
    uuid: string;
    address: string;
    city: string;
    consolidated_name: string;
    country_code: string;
    name: string;
    organization_number: number;
    professions: Array<string>;
    zip_code: string;
}

export interface PconLoginStoreState {
    user: PconLoginUser | null;
    organization: PconLoginOrganization | null;
    token_type: string | null;
    access_token: string | null;
    _package_groups: Array<string> | null;
    hide_package_groups: boolean;
    cache: Dict<string>;
}

class PconLoginApiUnauthorizedError extends utils.CustomError {
    constructor(message?: string) {
        super(message);
        Object.setPrototypeOf(this, PconLoginApiUnauthorizedError.prototype);
    }
}

class PconLoginStoreClass extends FluxUtils.ReduceStore<PconLoginStoreState, unknown> {
    public getInitialState(): PconLoginStoreState {
        return {
            user: null,
            organization: null,
            token_type: null,
            access_token: null,
            _package_groups: null,
            hide_package_groups: false,
            cache: {}
        };
    }

    public reduce(state: PconLoginStoreState, action: DispatcherBaseAction): PconLoginStoreState {
        // Note: this function should be `pure` -> we have to return a copy
        // of the current state if some property was changed

        switch (action.actionType) {
            case ActionConstants.PCON_LOGIN_LOGOUT:
                return jQuery.extend({}, this.getInitialState());
            case ActionConstants.SET_PCON_LOGIN_USER:
                return jQuery.extend({}, state, {
                    user: action.value
                });
            case ActionConstants.SET_PCON_LOGIN_ORGANIZATION:
                return jQuery.extend({}, state, {
                    organization: action.value
                });
            case ActionConstants.SET_PCON_LOGIN_TOKEN:
                return jQuery.extend({}, state, {
                    token_type: action.value.token_type,
                    access_token: action.value.access_token
                });
            case ActionConstants.UPDATE_PACKAGE_GROUPS:
                return jQuery.extend({}, state, {
                    _package_groups: action.value
                });
            case ActionConstants.HIDE_PACKAGE_GROUPS:
                return jQuery.extend({}, state, {
                    hide_package_groups: action.value
                });
            case ActionConstants.UPDATE_PCON_LOGIN_CACHE:
                return jQuery.extend({}, state, {
                    cache: action.value
                });
            default:
                break;
        }

        return state;
    }

    public hasPackageGroups(): boolean {
        return this.getPackageGroups().length > 0;
    }

    public pricesEnabled(): boolean {
        // return (AppConfigurationStore.getState().allowPrices && (!AppConfigurationStore.getState().pricesNeedLogin || this.isLoggedIn()));
        return (CustomLoginStore.getState().userInfo != null);
    }

    public getPackageGroups(): Array<string> {
        const state: PconLoginStoreState = this.getState();
        if (state._package_groups != null && state.hide_package_groups === false) {
            return state._package_groups;
        }

        return [];
    }

    public getUserId(): string | null {
        const state: PconLoginStoreState = this.getState();
        if (state.user != null && !utils.string.isNullOrEmpty(state.user.uuid)) {
            return state.user.uuid;
        }

        return null;
    }

    public getUserLogin(): string | null {
        const state: PconLoginStoreState = this.getState();
        if (state.user != null && !utils.string.isNullOrEmpty(state.user.login)) {
            return state.user.login;
        }

        return null;
    }

    public getUserEmail(): string | null {
        const state: PconLoginStoreState = this.getState();
        if (state.user != null && !utils.string.isNullOrEmpty(state.user.email)) {
            return state.user.email;
        }

        return null;
    }

    public isLoggedIn(): boolean {
        return (this.getState().access_token != null);
    }

    public getUserName(): string {
        const state: PconLoginStoreState = this.getState();
        let result: string = '';
        if (state.user != null) {
            if (state.user.first_name) {
                result = state.user.first_name;
                if (state.user.last_name) {
                    result += ' ' + state.user.last_name;
                }
            } else if (state.user.last_name) {
                result = state.user.last_name;
            }
        }
        return result;
    }

    public getIdentifier(): PconLoginIdentifier | undefined {
        const userId: string | null = this.getUserId();
        if (userId != null) {
            return {
                loginId: userId,
                loginName: this.getUserLogin(),
                loginEmail: this.getUserEmail()
            };
        }

        return;
    }

    public getAuthUrl(hideLogin: boolean = false): string {
        const pconLoginSettings: SettingProvider = new SettingProvider(PCON_LOGIN_CLIENT_ID, i18nextInstance);
        return hideLogin ? pconLoginSettings.backgroundAuthApiUrl : pconLoginSettings.iframeAuthApiUrl;
    }

    public apiRequest<T>(url: string, data: unknown | null = null, method: utils.async.AjaxMethod = 'POST', withCredentials: boolean = false): Promise<T> {
        return this.internalApiRequest<T>(
            url, data, method, withCredentials).catch((error: Error
        ): Promise<T> => {
            if (error instanceof PconLoginApiUnauthorizedError) {
                // renew the access token and retry the api request
                return PconLoginActions.backgroundLogin().then((): Promise<T> => {
                    return this.internalApiRequest<T>(url, data, method, withCredentials);
                });
            }
            throw error;
        });
    }

    protected internalApiRequest<T>(
        url: string, data: unknown | null = null, method: utils.async.AjaxMethod = 'POST', withCredentials: boolean = false
    ): Promise<T> {
        const state: PconLoginStoreState = this.getState();
        if (state.access_token == null || state.token_type == null) {
            return Promise.reject<T>(new Error('Invalid access token'));
        }

        let tMethod: utils.async.AjaxMethod = 'GET';
        let tData: string | null = null;
        const request: utils.async.AjaxOptions = {
            headers: {
                Authorization: [state.token_type, state.access_token].join(' ')
            }
        };

        if (data != null) {
            tMethod = method;
            tData = JSON.stringify(data);
            request.dataType = 'text';
            request.headers!['Content-Type'] = 'application/json; charset=utf-8';
        }

        if (withCredentials === true) {
            request.withCredentials = true;
        }

        return utils.async.ajax<T>(
            tMethod,
            url,
            tData,
            request,
            (pErrot: string | Error, xhr: XMLHttpRequest): Error | null => {
                if (xhr.status === 401) {
                    return new PconLoginApiUnauthorizedError();
                }

                return null;
            }
        ) as Promise<T>;
    }
}

export let PconLoginStore: PconLoginStoreClass = new PconLoginStoreClass(AppDispatcher);


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/stores/PconLoginStore.ts